/**
 * TradeHub popovers (mandate §7/§9) — composed surfaces, DEC-065 precedent.
 *
 * ACCOUNT SWITCHER: the design drew the chip and nothing behind it. The popover lists the session default
 * first — the account the server armed, which the client does not presume to name (ADR-016) — then every
 * account the server serves, with its plan and equity. One pick moves the whole desk: ticket, confirm draft,
 * positions view, balance strip.
 *
 * ADD SYMBOL: validated against the instrument contract, honest in both refusal directions — "already on the
 * desk" and "not in this deployment's instrument set" are different answers.
 */
import type { ViewModel } from '@/screens/generated/view-model';
import { css } from '@pointfive/ui';

const pop = css(
  'position:fixed;z-index:65;width:264px;border-radius:14px;border:1px solid var(--card-line);background:var(--surface);box-shadow:var(--shadow-float);padding:10px',
);
const under = css('position:fixed;inset:0;z-index:64');
const item = (on: boolean) =>
  css(
    `display:flex;flex-direction:column;align-items:stretch;gap:2px;width:100%;text-align:left;padding:8px 10px;border-radius:9px;border:none;cursor:pointer;background:${on ? 'var(--rail)' : 'transparent'};color:${on ? 'var(--rail-ink)' : 'var(--text-mid)'}`,
  );
const field = css(
  'width:100%;padding:7px 10px;border-radius:9px;border:1px solid var(--l10);background:var(--l2);color:var(--text);font-size:11.5px',
);
const goBtn = css(
  'font-size:11px;font-weight:600;padding:6px 13px;border-radius:99px;border:1px solid color-mix(in srgb, var(--slate-fill) 50%, transparent);background:color-mix(in srgb, var(--slate-fill) 10%, transparent);color:var(--slate);cursor:pointer',
);

export function TradeHubPopovers({ m }: { m: Partial<ViewModel> }) {
  return (
    <>
      {m.thAcctOpen ? (
        <>
          <div style={under} onClick={m.thAcctClose} aria-hidden="true" />
          <div style={{ ...pop, top: 108, right: 348 }} role="dialog" aria-label="Trading accounts">
            {(m.thAcctChoices ?? []).map((a) => (
              <button key={a.id ?? 'session'} style={item(a.on)} onClick={a.go} aria-pressed={a.on}>
                <span style={css('font-size:12px;font-weight:600;color:inherit')}>
                  {a.label}
                  {a.simulated ? (
                    <span style={css('font-size:9px;color:var(--muted);margin-left:6px')}>simulated</span>
                  ) : null}
                </span>
                <span style={css('font-size:10px;color:var(--muted)')}>{a.detail}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {m.deskAddOpen ? (
        <>
          <div style={under} onClick={m.deskAddToggle} aria-hidden="true" />
          <div style={{ ...pop, bottom: 120, right: 20 }} role="dialog" aria-label="Add symbol">
            <div style={css('font-size:10px;color:var(--muted);margin:0 0 8px;line-height:1.5')}>
              The desk lists what this deployment can quote. A symbol outside the instrument set is refused,
              not shown numberless.
            </div>
            <div style={css('display:flex;gap:6px')}>
              <input
                style={field}
                placeholder="e.g. EURUSD"
                aria-label="Symbol to add"
                value={String(m.deskAddInput ?? '')}
                onChange={(e) => m.deskAddSet?.(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') m.deskAddGo?.();
                }}
              />
              <button style={goBtn} onClick={m.deskAddGo} aria-label="Add this symbol">
                Add
              </button>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}
